/**
 * Boomfection Landing Page & Game Client — Orchestrator
 * Imports focused modules and wires everything together (init, events, socket)
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * XSS-SAFE: The remaining innerHTML usages in this orchestrator (loadLandingPageData,
 * onGamesListUpdate, onConnectionChange) sanitize all user-provided strings via
 * escapeHtml() from /js/ui/utils.js. Numeric/enum values come from the trusted API.
 * This is the same existing pattern as before the modular split — pure structural refactor.
 */

// Cache-Buster: v=20260403-1
import { initI18n, detectLocale, applyI18nToDOM, t } from '/js/i18n.js';
import { cookieConsent } from '/js/ui/CookieConsent.js';
import { adManager } from '/js/ads/AdManager.js';
import { Navigation } from '/js/ui/Navigation.js';
import {
    initAllEffects,
    disableBackgroundEffects,
    enableBackgroundEffects,
    createExplosionEffect
} from '/js/ui/BackgroundEffects.js';
import { initAuthModals } from '/js/ui/AuthModals.js';
import { PreferencesService } from '/js/ui/PreferencesService.js';
import { icon } from '/js/ui/icons.js';
await initI18n(detectLocale());
applyI18nToDOM();

// Replace emoji placeholders with SVG icons
document.querySelector('[data-icon-moon]')?.appendChild(icon('moon', 18));
document.querySelector('[data-icon-volume]')?.appendChild(icon('volume-2', 18));
document.querySelector('[data-icon-mute]')?.appendChild(icon('volume-x', 18));

// Init shared navigation (header + footer, no breadcrumb on landing)
await Navigation.init({ showNav: true, showFooter: true });

// Remove loading overlay once auth state is resolved
const _loadingOverlay = document.getElementById('page-loading-overlay');
if (_loadingOverlay) {
    _loadingOverlay.classList.add('fade-out');
    setTimeout(() => _loadingOverlay.remove(), 400);
}

import { MultiplayerClient } from '/js/game/MultiplayerClient.js';
import { SoundManager, SOUNDS } from '/js/game/SoundManager.js';
import { MapEditor, loadSavedMaps, saveMap, deleteSavedMap } from '/js/game/MapEditor.js';

// =========================================================================
// MODULE IMPORTS
// =========================================================================

import { applySeasonalTheme } from './ui/SeasonalTheme.js';
import { safeJsonFetch, getErrorMessage, showNotification } from './landing/LandingUtils.js';
import { initSeasonSelector, loadSeasonalLeaderboard, initExtendedLeaderboards } from './landing/LandingLeaderboard.js';
import { loadDailyQuests, loadWeeklyChallenges } from './landing/LandingQuests.js';
import { loadRecentGames, loadGameHighlight, initReplayButtons } from './landing/LandingRecentGames.js';
import {
    showConfetti,
    handleExplosionEffect,
    showKillFeed,
    showPillNotification,
    generateShareImage
} from './landing/LandingGameEffects.js';
import { addChatMessage, showTurnNotification } from './landing/LandingChat.js';
import {
    SLOT_TYPES,
    updateStartButton,
    updateLobbyPlayers,
    showSlotEditModal,
    updateStatsPanel,
    updateGameUI,
    startGameTimers,
    resetTurnTime,
    stopGameTimers
} from './landing/LandingGameUI.js';
import {
    initCameraReturnBtn,
    showCameraReturnBtn,
    hideCameraReturnBtn,
    setupTooltipEvents,
    updateFieldTooltip,
    getLastHoveredFieldId
} from './landing/LandingCamera.js';
import { renderCampaignLevels } from './landing/LandingCampaign.js';
import { loadFriendsList } from './landing/LandingFriends.js';
import {
    setupColorPicker,
    getSelectedColor,
    setupVirusPicker,
    updateRandomFillDisplay,
    setupSlotConfigurator
} from './landing/LandingPickers.js';
import { renderDailyRewardWidget } from './landing/LandingDailyReward.js';
import { initAchievementListener } from './landing/LandingAchievements.js';

// =========================================================================
// SEASONAL THEME
// =========================================================================

applySeasonalTheme();

// =========================================================================
// CLIENT SETUP
// =========================================================================

const canvas = document.getElementById('canvas');
if (!canvas) {
    console.error('[Boomfection] Canvas element not found! Waiting for DOM...');
}
const client = new MultiplayerClient(canvas);

/** @type {Object|null} Guest config from server */
let _guestConfig = null;

// Sound Manager Setup
const soundManager = new SoundManager();
let soundInitialized = false;

function initSound() {
    if (soundInitialized) return;
    if (soundManager.init()) {
        soundInitialized = true;
        soundManager.preload(SOUNDS);
    }
}

document.addEventListener('click', initSound, { once: true });
document.addEventListener('keydown', initSound, { once: true });

// Sound Controls UI
const soundToggleBtn = document.getElementById('btn-sound-toggle');
const soundVolumeSlider = document.getElementById('sound-volume');

soundToggleBtn?.classList.toggle('muted', !soundManager.isEnabled());
if (soundVolumeSlider) soundVolumeSlider.value = soundManager.getVolume() * 100;

if (soundToggleBtn) {
    soundToggleBtn.onclick = () => {
        initSound();
        const enabled = soundManager.toggle();
        soundToggleBtn.classList.toggle('muted', !enabled);
        if (enabled) {
            soundManager.play('buttonClick');
        }
    };
}

if (soundVolumeSlider) {
    soundVolumeSlider.oninput = (e) => {
        soundManager.setVolume(e.target.value / 100);
    };
}

// Game Settings Toggle (Explosion Speed, Sound Preset, Field Values)
const btnGameSettings = document.getElementById('btn-game-settings');
const gameSettingsDropdown = document.getElementById('game-settings-dropdown');
const explosionSpeedSlider = document.getElementById('explosion-speed');
const explosionSpeedLabel = document.getElementById('explosion-speed-label');
const soundPresetSelect = document.getElementById('sound-preset');
const showFieldValuesCheckbox = document.getElementById('show-field-values');

if (btnGameSettings) {
    btnGameSettings.onclick = () => {
        gameSettingsDropdown?.classList.toggle('hidden');
    };
    document.addEventListener('click', (e) => {
        if (!e.target.closest('#game-settings-toggle')) {
            gameSettingsDropdown?.classList.add('hidden');
        }
    });
}

// Explosion Speed
const savedExplosionSpeed = localStorage.getItem('boomfection_explosion_speed');
if (savedExplosionSpeed && explosionSpeedSlider) {
    explosionSpeedSlider.value = savedExplosionSpeed;
    if (explosionSpeedLabel) explosionSpeedLabel.textContent = `${savedExplosionSpeed}ms`;
}
if (explosionSpeedSlider) {
    explosionSpeedSlider.oninput = (e) => {
        const val = e.target.value;
        localStorage.setItem('boomfection_explosion_speed', val);
        if (explosionSpeedLabel) explosionSpeedLabel.textContent = `${val}ms`;
        PreferencesService.save('explosionSpeed', Number(val));
    };
}

// Sound Preset
const savedSoundPreset = localStorage.getItem('boomfection_sound_preset');
if (savedSoundPreset && soundPresetSelect) {
    soundPresetSelect.value = savedSoundPreset;
    soundManager.setPreset?.(savedSoundPreset);
}
if (soundPresetSelect) {
    soundPresetSelect.onchange = (e) => {
        const preset = e.target.value;
        localStorage.setItem('boomfection_sound_preset', preset);
        soundManager.setPreset?.(preset);
    };
}

// Show Field Values
const savedShowFieldValues = localStorage.getItem('boomfection_show_field_values');
if (showFieldValuesCheckbox) {
    showFieldValuesCheckbox.checked = savedShowFieldValues === 'true';
    showFieldValuesCheckbox.onchange = (e) => {
        localStorage.setItem('boomfection_show_field_values', e.target.checked);
        PreferencesService.save('showFieldValues', e.target.checked);
        if (client.virusRenderer) {
            client.virusRenderer.showFieldValues = e.target.checked;
        }
    };
}

// =========================================================================
// SHARED STATE
// =========================================================================

let selectedColor = 'red';
let selectedVirusType = 'classic';

// LocalStorage fuer Spieleinstellungen
const SETTINGS_KEY = 'boomfection_game_settings';

function loadSettings() {
    try {
        const data = localStorage.getItem(SETTINGS_KEY);
        if (data) {
            return JSON.parse(data);
        }
    } catch (e) {
        console.warn('[Settings] Could not load:', e);
    }
    return null;
}

function saveSettings() {
    try {
        const settings = {
            playerName: document.getElementById('create-name').value,
            colorId: getSelectedColor('create-colors'),
            virusType: selectedVirusType,
            maxPlayers: document.getElementById('create-max-players').value,
            subdivisions: document.getElementById('create-subdivisions').value,
            isPublic: document.getElementById('create-public').checked,
            randomFill: document.getElementById('create-random-fill').checked,
            randomFillMin: document.getElementById('random-fill-min').value,
            randomFillMax: document.getElementById('random-fill-max').value,
            pillsEnabled: document.getElementById('create-pills-enabled').checked,
            slots: slotConfigs.map((s) => ({ type: s.type, colorId: s.colorId, aiDifficulty: s.aiDifficulty }))
        };
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        // Sync game settings to server (fire-and-forget)
        PreferencesService.saveMultiple({
            playerName: settings.playerName,
            colorId: settings.colorId,
            virusType: settings.virusType,
            maxPlayers: settings.maxPlayers,
            subdivisions: settings.subdivisions,
            isPublic: settings.isPublic,
            pillsEnabled: settings.pillsEnabled,
            randomFill: settings.randomFill,
            slots: JSON.stringify(settings.slots)
        });
    } catch (e) {
        console.warn('[Settings] Could not save:', e);
    }
}

function applySettings(settings) {
    if (!settings) return;

    if (settings.playerName) {
        document.getElementById('create-name').value = settings.playerName;
        document.getElementById('join-name').value = settings.playerName;
    }
    if (settings.maxPlayers) {
        document.getElementById('create-max-players').value = settings.maxPlayers;
    }
    if (settings.subdivisions) {
        document.getElementById('create-subdivisions').value = settings.subdivisions;
    }
    if (settings.isPublic !== undefined) {
        document.getElementById('create-public').checked = settings.isPublic;
    }
    if (settings.randomFill !== undefined) {
        document.getElementById('create-random-fill').checked = settings.randomFill;
        if (settings.randomFill) {
            document.getElementById('random-fill-range-group').classList.remove('hidden');
        }
    }
    if (settings.randomFillMin !== undefined) {
        document.getElementById('random-fill-min').value = settings.randomFillMin;
    }
    if (settings.randomFillMax !== undefined) {
        document.getElementById('random-fill-max').value = settings.randomFillMax;
    }
    if (settings.pillsEnabled !== undefined) {
        document.getElementById('create-pills-enabled').checked = settings.pillsEnabled;
    }

    if (settings.colorId) selectedColor = settings.colorId;
    if (settings.virusType) selectedVirusType = settings.virusType;

    // Restore slot configurations
    if (settings.slots && Array.isArray(settings.slots)) {
        const maxPlayers = parseInt(document.getElementById('create-max-players').value, 10) || 6;
        slotConfigs = setupSlotConfigurator(maxPlayers);
        settings.slots.forEach((saved, i) => {
            if (i < slotConfigs.length && i > 0) { // Skip slot 0 (always player/host)
                if (saved.type) slotConfigs[i].type = saved.type;
                if (saved.colorId) slotConfigs[i].colorId = saved.colorId;
                if (saved.aiDifficulty) slotConfigs[i].aiDifficulty = saved.aiDifficulty;
            }
        });
    }
}

// Slot configurator (muss VOR applySettings deklariert sein)
let slotConfigs = [];

const savedSettings = loadSettings();
if (savedSettings) {
    applySettings(savedSettings);
}

// =========================================================================
// UI ELEMENTS
// =========================================================================

const statusEl = document.getElementById('connection-status');
const landingPage = document.getElementById('landing-page');
const mainMenu = document.getElementById('main-menu');
const createPanel = document.getElementById('create-panel');
const gamesListPanel = document.getElementById('games-list-panel');
const joinPanel = document.getElementById('join-panel');
const lobbyPanel = document.getElementById('lobby-panel');
const matchmakingPanel = document.getElementById('matchmaking-panel');
const campaignPanel = document.getElementById('campaign-panel');
const friendsPanel = document.getElementById('friends-panel');
const gameUI = document.getElementById('game-ui');
const chatPanel = document.getElementById('chat-panel');
const quickchatPanel = document.getElementById('quickchat-panel');

// Mobile: collapsed chat toggle via ::before pseudo-element click
if (chatPanel && window.matchMedia('(max-width: 768px)').matches) {
    chatPanel.addEventListener('click', (e) => {
        // Only toggle if clicking the header area (top 36px)
        const rect = chatPanel.getBoundingClientRect();
        if (e.clientY - rect.top < 40) {
            chatPanel.classList.toggle('chat-open');
        }
    });
}
const roundIndicator = document.getElementById('round-indicator');
const winnerOverlay = document.getElementById('winner-overlay');
const fieldTooltip = document.getElementById('field-tooltip');
const howToPlayModal = document.getElementById('how-to-play-modal');

// Auto-refresh fuer Landing Page
let landingRefreshInterval = null;
const LANDING_REFRESH_RATE = 5000;

function stopLandingRefresh() {
    if (landingRefreshInterval) {
        clearInterval(landingRefreshInterval);
        landingRefreshInterval = null;
    }
}

function showPanel(panel) {
    [
        landingPage,
        mainMenu,
        createPanel,
        gamesListPanel,
        joinPanel,
        lobbyPanel,
        matchmakingPanel,
        campaignPanel,
        gameUI
    ].forEach((p) => {
        if (p) p.classList.add('hidden');
    });
    if (panel) panel.classList.remove('hidden');

    if (panel !== landingPage) {
        stopLandingRefresh();
    }
}

/**
 * Cleans up spectator mode UI and state.
 * Safe to call when not in spectator mode.
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
function cleanupSpectatorMode() {
    if (!client.isSpectating) return;
    client.stopSpectating();
    _spectatorCount = 0;
    const bannerEl = document.getElementById('spectator-banner');
    if (bannerEl) bannerEl.remove();
    gameUI?.classList.remove('spectator-mode');
    winnerOverlay?.classList.add('hidden');
    enableBackgroundEffects();
}

function showLandingPage() {
    cleanupSpectatorMode();

    showPanel(landingPage);
    loadLandingPageData();

    stopLandingRefresh();
    landingRefreshInterval = setInterval(() => {
        loadLandingPageData();
    }, LANDING_REFRESH_RATE);
}

// =========================================================================
// SOCIAL PROOF COUNT-UP ANIMATION
// =========================================================================

const _proofAnimated = new Set();

/**
 * Animates a counter element from 0 (or current value) to the target number
 * @param {string} elementId
 * @param {number} target
 */
function animateCounter(elementId, target) {
    const el = document.getElementById(elementId);
    if (!el) return;
    target = Math.max(0, Math.floor(target));
    const current = parseInt(el.textContent, 10) || 0;
    if (current === target) return;
    // Only animate from 0 on first load
    const start = _proofAnimated.has(elementId) ? current : 0;
    _proofAnimated.add(elementId);
    if (target === 0) { el.textContent = '0'; return; }
    const duration = 1200;
    const startTime = performance.now();
    function step(now) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.floor(start + (target - start) * eased);
        if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
}

// =========================================================================
// LANDING PAGE DATA
// =========================================================================

async function loadLandingPageData() {
    try {
        const stats = await safeJsonFetch('/api/stats');
        if (!stats) return;
        document.getElementById('stat-online').textContent = stats.onlinePlayers || 0;
        document.getElementById('stat-games').textContent = stats.activeGames || 0;

        // Social proof counters with count-up animation
        animateCounter('proof-players', stats.totalRegisteredUsers || 0);
        animateCounter('proof-games', stats.totalGamesPlayed || 0);
        animateCounter('proof-online', stats.onlinePlayers || 0);

        const gamesContainer = document.getElementById('active-games-content');
        if (stats.games && stats.games.length > 0) {
            // Build game rows using DOM methods for XSS safety
            gamesContainer.textContent = '';
            const fragment = document.createDocumentFragment();
            stats.games.forEach((g) => {
                const row = document.createElement('div');
                row.className = 'game-row';
                row.dataset.gameId = g.id;

                const info = document.createElement('div');
                info.className = 'game-info';
                const nameDiv = document.createElement('div');
                nameDiv.className = 'game-name';
                nameDiv.textContent = g.name || t('game.gameId', { id: g.id });
                const hostDiv = document.createElement('div');
                hostDiv.className = 'game-host';
                hostDiv.textContent = `${t('lobby.host')}: ${g.hostName}`;
                info.appendChild(nameDiv);
                info.appendChild(hostDiv);

                const meta = document.createElement('div');
                meta.className = 'game-meta';
                const playersDiv = document.createElement('div');
                playersDiv.className = 'game-players';
                playersDiv.textContent = `${g.players}/${g.maxPlayers}`;
                const stateDiv = document.createElement('div');
                stateDiv.className = `game-state ${g.state}`;
                stateDiv.textContent = g.state === 'waiting' ? t('game.stateWaiting') : t('game.statePlaying');
                meta.appendChild(playersDiv);
                meta.appendChild(stateDiv);

                row.appendChild(info);
                row.appendChild(meta);

                // Spectate button for running games on landing page
                if (g.state === 'playing') {
                    const spectBtn = document.createElement('button');
                    spectBtn.className = 'spectate-btn small';
                    spectBtn.textContent = t('game.spectate');
                    spectBtn.onclick = (e) => {
                        e.stopPropagation();
                        client.spectateGame(g.id);
                    };
                    meta.appendChild(spectBtn);
                }

                // Spectator count badge on landing page
                if (g.spectatorCount > 0) {
                    const specBadge = document.createElement('span');
                    specBadge.className = 'spectator-badge';
                    specBadge.textContent = `\u{1F441} ${g.spectatorCount}`;
                    meta.appendChild(specBadge);
                }

                row.onclick = () => {
                    if (g.state === 'playing') {
                        client.spectateGame(g.id);
                        return;
                    }
                    document.getElementById('join-code').value = g.id;
                    showPanel(joinPanel);
                    loadGameColorsForJoin(g.id);
                };

                fragment.appendChild(row);
            });
            gamesContainer.appendChild(fragment);
        } else {
            gamesContainer.textContent = '';
            const empty = document.createElement('div');
            empty.className = 'games-empty';
            empty.textContent = t('game.noActiveGames');
            gamesContainer.appendChild(empty);
        }

        await initSeasonSelector();
        loadSeasonalLeaderboard('wins');
        loadDailyQuests();
        loadWeeklyChallenges();
        loadRecentGames();
        loadGameHighlight();
        initExtendedLeaderboards();
    } catch (err) {
        console.error('[Landing] Failed to load stats:', err);
    }
}

// =========================================================================
// PICKERS SETUP
// =========================================================================

/** Callback wrapper for color picker selection */
function onColorSelect(colorId) {
    selectedColor = colorId;
}

/** Callback wrapper for virus picker selection */
function onVirusSelect(virusId) {
    selectedVirusType = virusId;
}

setupColorPicker('create-colors', savedSettings?.colorId || 'red', [], onColorSelect);
setupColorPicker('join-colors', savedSettings?.colorId || 'blue', [], onColorSelect);
setupVirusPicker('create-virus-type', savedSettings?.virusType || 'classic', onVirusSelect);
setupVirusPicker('join-virus-type', savedSettings?.virusType || 'classic', onVirusSelect);

if (savedSettings?.randomFillMin !== undefined && savedSettings?.randomFillMax !== undefined) {
    document.getElementById('random-fill-range-value').textContent =
        `${savedSettings.randomFillMin}-${savedSettings.randomFillMax}`;
}

// Random Fill Range Slider
const randomFillCheckbox = document.getElementById('create-random-fill');
const randomFillRangeGroup = document.getElementById('random-fill-range-group');
const randomFillMin = document.getElementById('random-fill-min');
const randomFillMax = document.getElementById('random-fill-max');

if (randomFillCheckbox) {
    randomFillCheckbox.onchange = () => {
        randomFillRangeGroup?.classList.toggle('hidden', !randomFillCheckbox.checked);
    };
}

if (randomFillMin) {
    randomFillMin.oninput = () => {
        if (randomFillMax && parseInt(randomFillMin.value) >= parseInt(randomFillMax.value)) {
            randomFillMax.value = Math.min(6, parseInt(randomFillMin.value) + 1);
        }
        updateRandomFillDisplay();
    };
}

if (randomFillMax) {
    randomFillMax.oninput = () => {
        if (randomFillMin && parseInt(randomFillMax.value) <= parseInt(randomFillMin.value)) {
            randomFillMin.value = Math.max(0, parseInt(randomFillMax.value) - 1);
        }
        updateRandomFillDisplay();
    };
}

// Slot configurator initialisieren
slotConfigs = setupSlotConfigurator(parseInt(document.getElementById('create-max-players').value));
document.getElementById('create-max-players').onchange = (e) => {
    slotConfigs = setupSlotConfigurator(parseInt(e.target.value));
};

// =========================================================================
// CLIENT CALLBACKS
// =========================================================================

/**
 * Ping/Latenz-Messung und -Anzeige
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
let currentPing = 0;
let pingInterval = null;
let lastPingStart = 0;
let pongListenerAttached = false;

function startPingMeasurement() {
    if (pingInterval) clearInterval(pingInterval);

    // Einziger dauerhafter Pong-Listener — kein Handler-Leak bei Paketverlust
    if (!pongListenerAttached && client.socket) {
        client.socket.on('pong-check', () => {
            if (lastPingStart === 0) return; // Kein offener Ping
            currentPing = Date.now() - lastPingStart;
            lastPingStart = 0;
            const badge = document.getElementById('ping-badge');
            if (badge) badge.textContent = `${currentPing}ms`;
        });
        pongListenerAttached = true;
    }

    pingInterval = setInterval(() => {
        if (!client.socket || !client.socket.connected) return;
        lastPingStart = Date.now();
        client.socket.emit('ping-check');
    }, 5000);
}

/** @type {number|null} */
let reconnectCountdown = null;

client.onConnectionChange = (state) => {
    statusEl.className = `connection-status ${state}`;
    // Build status text + dot + ping badge via DOM methods
    statusEl.textContent = '';

    // Pulsing dot indicator
    const dot = document.createElement('span');
    dot.className = 'connection-dot';
    statusEl.appendChild(dot);

    const stateText = state === 'connected' ? t('game.online') : state === 'disconnected' ? t('game.offline') : t('common.connecting');
    statusEl.appendChild(document.createTextNode(stateText));

    if (state === 'connected') {
        const pingSpan = document.createElement('span');
        pingSpan.id = 'ping-badge';
        pingSpan.className = 'ping-badge';
        pingSpan.textContent = '--ms';
        statusEl.appendChild(document.createTextNode(' '));
        statusEl.appendChild(pingSpan);
    }

    // Auto-reconnect countdown for disconnected state
    if (reconnectCountdown) {
        clearInterval(reconnectCountdown);
        reconnectCountdown = null;
    }
    if (state === 'disconnected') {
        let seconds = 5;
        const countdownEl = document.createElement('span');
        countdownEl.className = 'connection-countdown';
        countdownEl.textContent = ` (${seconds}s)`;
        statusEl.appendChild(countdownEl);
        reconnectCountdown = setInterval(() => {
            seconds--;
            if (seconds <= 0) {
                clearInterval(reconnectCountdown);
                reconnectCountdown = null;
                countdownEl.textContent = '';
            } else {
                countdownEl.textContent = ` (${seconds}s)`;
            }
        }, 1000);
    }

    if (state === 'connected') startPingMeasurement();
    else if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
    }
};

const gamesCache = new Map();

client.onGamesListUpdate = (games) => {
    const list = document.getElementById('games-list');
    gamesCache.clear();
    games.forEach((g) => gamesCache.set(g.id, g));

    if (games.length === 0) {
        list.textContent = '';
        const noGames = document.createElement('div');
        noGames.className = 'no-games';
        noGames.textContent = t('game.noOpenGames');
        list.appendChild(noGames);
    } else {
        list.textContent = '';
        const fragment = document.createDocumentFragment();
        games.forEach((g) => {
            const isPlaying = g.state === 'playing' || g.state === 'explosion';
            const item = document.createElement('div');
            item.className = `game-list-item ${g.hasPassword ? 'has-password' : ''}${isPlaying ? ' is-playing' : ''}`;
            item.dataset.gameId = g.id;
            item.dataset.hasPassword = g.hasPassword;
            item.dataset.state = g.state || 'lobby';

            const infoDiv = document.createElement('div');
            infoDiv.className = 'info';

            const hostDiv = document.createElement('div');
            hostDiv.className = 'host';
            if (g.hasPassword) {
                const lockSpan = document.createElement('span');
                lockSpan.className = 'lock-icon';
                lockSpan.textContent = '\u{1F512}';
                hostDiv.appendChild(lockSpan);
                hostDiv.appendChild(document.createTextNode(' '));
            }
            hostDiv.appendChild(document.createTextNode(g.hostName));
            if (isPlaying) {
                const statusBadge = document.createElement('span');
                statusBadge.className = 'badge badge-playing';
                statusBadge.textContent = t('game.live');
                hostDiv.appendChild(statusBadge);
            }
            if (isPlaying && g.spectatorCount > 0) {
                const specBadge = document.createElement('span');
                specBadge.className = 'badge badge-spectator';
                specBadge.textContent = t('game.watching', { n: g.spectatorCount });
                hostDiv.appendChild(specBadge);
            }

            const metaDiv = document.createElement('div');
            metaDiv.className = 'meta';
            let playerInfo = `${g.playerCount}/${g.maxPlayers}`;
            if (g.aiCount > 0) {
                playerInfo += ` (${g.humanCount || g.playerCount - g.aiCount} + ${g.aiCount} ${t('lobby.aiBadge')})`;
            }
            playerInfo += ` ${t('game.players')}`;
            if (g.isPrivate) playerInfo += ` (${t('game.private')})`;
            metaDiv.textContent = playerInfo;

            infoDiv.appendChild(hostDiv);
            infoDiv.appendChild(metaDiv);
            item.appendChild(infoDiv);

            if (!isPlaying) {
                const joinBtn = document.createElement('button');
                joinBtn.className = 'join-btn';
                joinBtn.textContent = t('common.join');
                joinBtn.onclick = async (e) => {
                    e.stopPropagation();
                    const gameId = item.dataset.gameId;
                    const hasPassword = item.dataset.hasPassword === 'true';

                    document.getElementById('join-code').value = gameId;
                    const passwordGroup = document.getElementById('join-password-group');
                    const passwordInput = document.getElementById('join-password');
                    passwordGroup.classList.toggle('hidden', !hasPassword);
                    passwordInput.value = '';

                    await loadGameColorsForJoin(gameId);
                    showPanel(joinPanel);
                };
                item.appendChild(joinBtn);
            } else {
                // Spectate button for running games
                const spectateBtn = document.createElement('button');
                spectateBtn.className = 'spectate-btn';
                spectateBtn.textContent = t('game.spectate');
                spectateBtn.onclick = (e) => {
                    e.stopPropagation();
                    client.spectateGame(g.id);
                };
                item.appendChild(spectateBtn);
            }

            fragment.appendChild(item);
        });
        list.appendChild(fragment);
    }
};

/** Shorthand wrappers that pass client to module functions */
function _updateStartButton() {
    updateStartButton(client);
}
function _updateLobbyPlayers() {
    updateLobbyPlayers(client, (idx) => showSlotEditModal(idx, client));
}
function _updateStatsPanel() {
    updateStatsPanel(client);
}
function _updateGameUI() {
    updateGameUI(client);
}
function _addChatMessage(msg) {
    addChatMessage(msg, { socket: client.socket });
}

client.onGameCreated = (data) => {
    document.getElementById('lobby-code').textContent = data.gameId;
    _updateLobbyPlayers();
    showPanel(lobbyPanel);
    chatPanel.classList.remove('hidden');
    _updateStartButton();
};

client.onGameJoined = (data) => {
    document.getElementById('lobby-code').textContent = data.gameId;
    const gameState = client.game?.state;
    if (data.isReconnect && (gameState === 'playing' || gameState === 'explosion')) {
        showPanel(gameUI);
        chatPanel.classList.remove('hidden');
        quickchatPanel.classList.remove('hidden');
        roundIndicator.classList.add('hidden');
        document.getElementById('stats-panel').classList.remove('hidden');
        _updateGameUI();
        _updateStatsPanel();
        startGameTimers();
        _addChatMessage({ text: t('game.reconnected'), isSystem: true });
    } else {
        _updateLobbyPlayers();
        showPanel(lobbyPanel);
        chatPanel.classList.remove('hidden');
        _updateStartButton();
    }
};

client.onPlayerJoined = (player) => {
    _updateLobbyPlayers();
    _addChatMessage({ text: t('game.playerJoined', { name: player.name }), isSystem: true });
    soundManager.play('playerJoined');
};
client.onPlayerLeft = (data) => {
    _updateLobbyPlayers();
    _addChatMessage({ text: t('game.playerLeft', { name: data.playerName }), isSystem: true });
    soundManager.play('playerLeft');
};
client.onPlayerUpdated = (data) => {
    _updateLobbyPlayers();
    _updateGameUI();
    if (data?.disconnected) {
        const player = client.game?.players.get(data.playerId);
        if (player && !player.isAI) {
            _addChatMessage({ text: t('game.playerDisconnected', { name: player.name }), isSystem: true });
        }
    } else if (data?.reconnected) {
        const player = client.game?.players.get(data.playerId);
        if (player) {
            _addChatMessage({ text: t('game.playerReconnected', { name: player.name }), isSystem: true });
        }
    }
};

client.onGameStarted = () => {
    showPanel(gameUI);
    chatPanel.classList.remove('hidden');
    quickchatPanel.classList.remove('hidden');
    roundIndicator.classList.add('hidden');
    document.getElementById('stats-panel').classList.remove('hidden');
    _updateGameUI();
    _updateStatsPanel();
    startGameTimers();
    _addChatMessage({ text: t('game.started'), isSystem: true });
    soundManager.play('gameStart');
    disableBackgroundEffects();
};

// Chain explosion counter (shared mutable state for handleExplosionEffect)
let chainExplosionCount = 0;
let opponentMoveCentered = false;

/** Context object passed to handleExplosionEffect */
const effectCtx = {
    get client() {
        return client;
    },
    get soundManager() {
        return soundManager;
    },
    get chainExplosionCount() {
        return chainExplosionCount;
    },
    set chainExplosionCount(v) {
        chainExplosionCount = v;
    }
};

client.onTurnChange = (data) => {
    chainExplosionCount = 0;
    opponentMoveCentered = false;

    document.getElementById('round-number').textContent = data.round;
    document.getElementById('stats-round-number').textContent = data.round;
    _updateGameUI();
    _updateStatsPanel();

    const isMyTurn = data.currentPlayerId === client.playerId;
    const delay = isMyTurn ? 1500 : 0;

    setTimeout(() => {
        resetTurnTime();
        const currentPlayer = client.game?.players.get(data.currentPlayerId);
        if (currentPlayer) {
            showTurnNotification(currentPlayer.name, currentPlayer.color?.hex, isMyTurn);
        }

        if (isMyTurn) {
            const turnBox = document.getElementById('current-turn-box');
            if (turnBox) {
                turnBox.classList.add('animate-bounce');
                setTimeout(() => turnBox.classList.remove('animate-bounce'), 500);
            }
            soundManager.play('yourTurn');
            showCameraReturnBtn();
            // Undo-Button verstecken wenn man wieder am Zug ist
            if (btnUndo) btnUndo.style.display = 'none';
        } else if (btnUndo && !btnUndo._used) {
            // Nach eigenem Zug: Undo-Button zeigen (1x pro Spiel, nicht in Ranked/Speed)
            btnUndo.style.display = '';
            btnUndo.disabled = false;
        }
    }, delay);
};

let aiThinkingPlayerId = null;
client.onAIThinking = (data) => {
    aiThinkingPlayerId = data.playerId;

    if (client.renderer && data.targetFieldId != null) {
        client.renderer.rotateToField(data.targetFieldId);
        client.renderer.setAITarget(data.targetFieldId);
    }

    const playerItems = document.querySelectorAll('#game-player-list .player-item');
    playerItems.forEach((item) => {
        const playerName = item.querySelector('.player-name')?.textContent;
        const player = client.game?.players.get(data.playerId);
        if (player && playerName === player.name) {
            item.classList.add('thinking');
            if (!item.querySelector('.thinking-badge')) {
                const badge = document.createElement('span');
                badge.className = 'player-badge thinking thinking-badge';
                badge.textContent = t('game.aiThinking');
                item.appendChild(badge);
            }
        }
    });
};

client.onAIMove = (data) => {
    if (aiThinkingPlayerId === data.playerId) {
        aiThinkingPlayerId = null;
        if (client.renderer) {
            client.renderer.clearAITarget();
        }
        const playerItems = document.querySelectorAll('#game-player-list .player-item');
        playerItems.forEach((item) => {
            item.classList.remove('thinking');
            const thinkingBadge = item.querySelector('.thinking-badge');
            if (thinkingBadge) thinkingBadge.remove();
        });
    }
};

client.onPointsUpdate = (data) => {
    if (data.playerId === client.playerId && data.change > 0) {
        const pointsValue = document.getElementById('my-points');
        const pointsChange = document.getElementById('points-change');
        pointsValue.textContent = data.points;
        pointsChange.textContent = `+${data.change}`;
        pointsChange.classList.remove('hidden', 'negative');
        pointsChange.style.animation = 'none';
        pointsChange.offsetHeight;
        pointsChange.style.animation = null;
        setTimeout(() => {
            pointsChange.classList.add('hidden');
        }, 600);
        if (data.change >= 10) soundManager.play('chainReaction');
    }
    _updateStatsPanel();
};

client.onFieldUpdate = (fieldData, oldData = {}) => {
    _updateGameUI();
    _updateStatsPanel();
    if (getLastHoveredFieldId() === fieldData.id) updateFieldTooltip(fieldData.id, client);

    if (fieldData.ownerId && fieldData.ownerId !== client.playerId && !opponentMoveCentered) {
        const owner = client.game?.players.get(fieldData.ownerId);
        const isAI = owner?.isAI === true;
        if (!isAI) {
            if (client.renderer?.rotateToField) {
                client.renderer.rotateToField(fieldData.id);
            }
        }
        opponentMoveCentered = true;
    }

    if (oldData.oldOwner !== undefined && oldData.oldOwner !== fieldData.ownerId && fieldData.ownerId) {
        if (fieldData.ownerId === client.playerId) {
            soundManager.play('fieldCaptured');
        } else if (oldData.oldOwner === client.playerId) {
            soundManager.play('fieldLost');
        } else {
            soundManager.play('fieldCaptured', { volume: 0.3 });
        }
        if (client.virusRenderer) {
            const newPlayer = client.game?.players.get(fieldData.ownerId);
            const oldPlayer = oldData.oldOwner ? client.game?.players.get(oldData.oldOwner) : null;
            client.virusRenderer.triggerConquestWave(
                fieldData.id,
                newPlayer?.color?.hex || '#ffffff',
                oldPlayer?.color?.hex || null
            );
        }
    } else if (fieldData && fieldData.value > 0 && fieldData.value > (oldData.oldValue || 0)) {
        const isOwnField = fieldData.ownerId === client.playerId;
        soundManager.play('placeVirus', { volume: isOwnField ? 1.0 : 0.4 });
    }
};

client.onExplosion = (data) => {
    handleExplosionEffect(data, false, effectCtx);
};

client.onTickComplete = (data) => {
    for (const fieldId of data.explosions) {
        handleExplosionEffect(data, true, effectCtx, fieldId);
    }

    // Chain Reaction Camera: follow explosions for chains >= 5
    const explosionCount = data.explosions?.length ?? 0;
    const followChainsEnabled = localStorage.getItem('boomfection_follow_chains') !== 'false';
    if (followChainsEnabled && explosionCount >= 5 && client.renderer) {
        const fieldIds = data.explosions.map((e) => (typeof e === 'object' ? e.fieldId ?? e.id : e));
        client.renderer.followChain(fieldIds);

        // Epic chain (>= 10): add subtle zoom class to game container
        if (explosionCount >= 10) {
            const canvasContainer = document.getElementById('canvas-container');
            if (canvasContainer) {
                canvasContainer.classList.add('epic-chain');
                // Duration: chain follow time + small buffer
                const epicDuration = fieldIds.length * 300 + 800;
                setTimeout(() => canvasContainer.classList.remove('epic-chain'), epicDuration);
            }
        }
    }
};

client.onPlayerEliminated = (data) => {
    _addChatMessage({ text: t('game.playerEliminated', { name: data.playerName }), isSystem: true });
    _updateGameUI();
    _updateStatsPanel();
    soundManager.play('playerEliminated');

    const currentPlayer = client.game?.getCurrentPlayer ? client.game.getCurrentPlayer() : null;
    const killerName = currentPlayer ? currentPlayer.name : t('game.chainReactionKiller');
    const killerColor = currentPlayer?.color?.hex || '#ff0044';
    showKillFeed(killerName, data.playerName, killerColor);

    const elimOverlay = document.getElementById('elimination-overlay');
    const elimPlayer = elimOverlay.querySelector('.elimination-player');
    const player = client.game?.players.get(data.playerId);
    const playerColor = player?.color?.hex || '#ff3333';
    elimPlayer.textContent = data.playerName;
    elimPlayer.style.setProperty('--player-color', playerColor);
    elimOverlay.classList.remove('hidden');

    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
        canvasContainer.classList.add('screen-shake');
        setTimeout(() => canvasContainer.classList.remove('screen-shake'), 500);
    }

    if (client.virusRenderer && player) {
        for (let i = 0; i < 5; i++) {
            setTimeout(() => {
                const canvas = client.virusRenderer?.canvas;
                if (!canvas || !client.virusRenderer) return;
                client.virusRenderer.createEliminationParticles(
                    Math.random() * canvas.clientWidth,
                    Math.random() * canvas.clientHeight,
                    playerColor
                );
            }, i * 100);
        }
    }
    setTimeout(() => elimOverlay.classList.add('hidden'), 1500);
};

client.onGameEnded = (data) => {
    stopGameTimers();

    // Spectator mode: hide banner when game ends
    if (client.isSpectating) {
        gameUI.classList.remove('spectator-mode');
    }

    winnerOverlay.classList.remove('hidden');
    const winnerName = document.getElementById('winner-name');
    const winnerTitle = document.getElementById('winner-title');
    const _confettiContainer = document.getElementById('confetti-container');
    const pointsWinnerSection = document.getElementById('points-winner-section');
    const pointsWinnerName = document.getElementById('points-winner-name');
    const pointsWinnerScore = document.getElementById('points-winner-score');
    const finalRankingList = document.getElementById('final-ranking-list');

    const isVictory = data.winner && data.winner.id === client.playerId;
    const isPointsVictory = data.pointsWinner && data.pointsWinner.id === client.playerId;

    if (data.winner) {
        winnerName.textContent = t('game.winnerAnnounce', { name: data.winner.name });
        winnerName.style.color = data.winner.color?.hex || '#fff';
        if (isVictory) {
            winnerTitle.textContent = t('game.victory');
            winnerTitle.classList.add('victory-title');
            soundManager.playVictoryFanfare();
            showConfetti();
            showConfetti();
            winnerOverlay.classList.add('victory-glow');
        } else {
            winnerTitle.textContent = t('game.defeat');
            winnerTitle.classList.remove('victory-title');
            winnerOverlay.classList.remove('victory-glow');
            soundManager.play('defeat');
            showConfetti();
        }
    } else {
        winnerTitle.textContent = t('game.ended');
        winnerTitle.classList.remove('victory-title');
        winnerOverlay.classList.remove('victory-glow');
        winnerName.textContent = t('game.draw');
        winnerName.style.color = '#888';
        soundManager.play('defeat');
    }

    if (data.isDifferentWinner && data.pointsWinner) {
        pointsWinnerSection.classList.remove('hidden');
        pointsWinnerName.textContent = data.pointsWinner.name;
        pointsWinnerName.style.color = data.pointsWinner.color?.hex || '#ffd700';
        pointsWinnerScore.textContent = `${data.pointsWinner.points || 0} ${t('game.pointsFull')}`;
        if (isPointsVictory && !isVictory) pointsWinnerSection.classList.add('my-points-victory');
    } else {
        pointsWinnerSection.classList.add('hidden');
    }

    if (data.players && data.players.length > 0) {
        const sortedPlayers = data.players.slice().sort((a, b) => (b.points || 0) - (a.points || 0));
        finalRankingList.textContent = '';
        sortedPlayers.forEach((player, index) => {
            const rankItem = document.createElement('div');
            rankItem.className = 'final-rank-item';
            if (player.id === client.playerId) rankItem.classList.add('my-rank');
            const medal =
                index === 0 ? '\u{1F947}' : index === 1 ? '\u{1F948}' : index === 2 ? '\u{1F949}' : `${index + 1}.`;
            const isGameWinner = data.winner && player.id === data.winner.id;

            const posSpan = document.createElement('span');
            posSpan.className = 'rank-position';
            posSpan.textContent = medal;
            const nameSpan = document.createElement('span');
            nameSpan.className = 'rank-name';
            nameSpan.style.color = player.color?.hex || '#fff';
            nameSpan.textContent = player.name;
            const ptsSpan = document.createElement('span');
            ptsSpan.className = 'rank-points';
            ptsSpan.textContent = `${player.points || 0} ${t('game.pointsShort')}`;

            rankItem.appendChild(posSpan);
            rankItem.appendChild(nameSpan);
            rankItem.appendChild(ptsSpan);
            if (isGameWinner) {
                const crownSpan = document.createElement('span');
                crownSpan.className = 'rank-crown';
                crownSpan.textContent = '\u{1F451}';
                rankItem.appendChild(crownSpan);
            }
            finalRankingList.appendChild(rankItem);
        });
    }

    document.getElementById('stat-rounds').textContent = data.rounds || '-';
    if (data.duration) {
        const mins = Math.floor(data.duration / 60000);
        const secs = Math.floor((data.duration % 60000) / 1000);
        document.getElementById('stat-duration').textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    } else {
        document.getElementById('stat-duration').textContent = '-';
    }

    renderPlayerStatsDetail(data.players);

    enableBackgroundEffects();
    const rematchBtn = document.getElementById('btn-rematch');
    // Hide rematch for spectators
    if (client.isSpectating) {
        rematchBtn.style.display = 'none';
    } else {
        rematchBtn.style.display = '';
        rematchBtn.disabled = false;
        rematchBtn.textContent = t('game.rematch');
    }

    // Share button
    let shareBtn = document.getElementById('btn-share');
    if (!shareBtn) {
        shareBtn = document.createElement('button');
        shareBtn.id = 'btn-share';
        shareBtn.className = 'btn btn-secondary';
        shareBtn.textContent = t('game.share');
        shareBtn.style.marginTop = '8px';
        rematchBtn.parentNode.insertBefore(shareBtn, rematchBtn.nextSibling);
    }
    shareBtn.style.display = '';
    shareBtn.onclick = () => {
        const wName = data.winner ? data.winner.name : 'Unentschieden';
        const wColor = data.winner?.color?.hex || '#00ff88';
        const mapFields = client.game?.sphere?.fields?.size || '?';
        const longest = data.longestChain || chainExplosionCount || '-';
        const shareCanvas = generateShareImage(wName, mapFields, longest, wColor);
        shareCanvas.toBlob((blob) => {
            if (!blob) return;
            try {
                navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).then(
                    () => showNotification(t('game.imageCopied'), 'success'),
                    () => showNotification(t('game.shareFailed'), 'error')
                );
            } catch {
                /* expected — Clipboard API not supported in this browser */
                showNotification(t('game.shareNotSupported'), 'error');
            }
        }, 'image/png');
    };
};

client.onChatMessage = (msg) => {
    _addChatMessage(msg);
    if (msg.isQuickChat) soundManager.play('quickChatGG');
    else if (!msg.isSystem) soundManager.play('chatMessage');
};

client.onError = (type, error) => {
    showNotification(getErrorMessage(error), 'error');
    if (error === 'invalid_move' || error === 'field_not_clickable') {
        soundManager.playInvalidMove();
    } else {
        soundManager.play('error');
    }
};

// =========================================================================
// SPECTATOR MODE
// @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
// =========================================================================

/** Current spectator count for the game being watched */
let _spectatorCount = 0;

client.onSpectateState = (data) => {
    // Switch to game view in spectator mode
    showPanel(gameUI);
    disableBackgroundEffects();

    // Show spectator banner
    let banner = document.getElementById('spectator-banner');
    if (!banner) {
        banner = document.createElement('div');
        banner.id = 'spectator-banner';
        banner.className = 'spectator-banner';
        gameUI.prepend(banner);
    }

    // Build player names for banner
    const playerNames = (data.players || [])
        .filter((p) => !p.isEliminated)
        .map((p) => p.name)
        .join(' vs ');
    banner.textContent = '';
    const bannerText = document.createElement('span');
    bannerText.textContent = `${t('game.spectatorBanner')} \u2014 ${playerNames}`;
    banner.appendChild(bannerText);

    const specCountEl = document.createElement('span');
    specCountEl.id = 'spectator-count-display';
    specCountEl.className = 'spectator-count';
    specCountEl.textContent = '';
    banner.appendChild(specCountEl);

    const backBtn = document.createElement('button');
    backBtn.className = 'btn btn-small spectator-back-btn';
    backBtn.textContent = t('game.stopSpectating');
    backBtn.onclick = () => {
        cleanupSpectatorMode();
        showLandingPage();
    };
    banner.appendChild(backBtn);

    banner.classList.remove('hidden');

    // Hide action controls (chat input, quickchat, start button etc.)
    chatPanel.classList.add('hidden');
    quickchatPanel.classList.add('hidden');
    const exitBtn = document.getElementById('btn-exit-game');
    if (exitBtn) exitBtn.classList.add('hidden');

    // Show stats panel and round indicator
    document.getElementById('stats-panel').classList.remove('hidden');
    roundIndicator.classList.add('hidden');

    // Add spectator class to game UI for CSS targeting
    gameUI.classList.add('spectator-mode');

    _updateGameUI();
    _updateStatsPanel();

    if (data.currentRound) {
        document.getElementById('round-number').textContent = data.currentRound;
        document.getElementById('stats-round-number').textContent = data.currentRound;
    }
};

client.onSpectateError = (_data) => {
    showNotification(t('game.spectateError'), 'error');
};

client.onSpectatorCount = (data) => {
    _spectatorCount = data.count || 0;
    const countEl = document.getElementById('spectator-count-display');
    if (countEl) {
        countEl.textContent = _spectatorCount > 0 ? t('game.spectators', { count: _spectatorCount }) : '';
    }
};

/**
 * Registriert Socket-Events fuer Chat-Reaktionen und Rematch
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
function setupChatReactionAndRematchEvents() {
    if (!client.socket) return;

    client.socket.on('chat:reaction', (data) => {
        if (!data || !data.messageId || !data.emoji) return;
        const msgEl = document.querySelector(`.chat-message[data-message-id="${CSS.escape(data.messageId)}"]`);
        if (!msgEl) return;

        let badges = msgEl.querySelector('.reaction-badges');
        if (!badges) {
            badges = document.createElement('div');
            badges.className = 'reaction-badges';
            msgEl.appendChild(badges);
        }

        let badge = badges.querySelector(`[data-emoji="${data.emoji}"]`);
        if (badge) {
            const countEl = badge.querySelector('.reaction-count');
            countEl.textContent = parseInt(countEl.textContent || '1') + 1;
        } else {
            badge = document.createElement('span');
            badge.className = 'reaction-badge';
            badge.dataset.emoji = data.emoji;
            badge.textContent = data.emoji;
            const countSpan = document.createElement('span');
            countSpan.className = 'reaction-count';
            countSpan.textContent = '1';
            badge.appendChild(countSpan);
            badge.title = data.playerName;
            badges.appendChild(badge);
        }
    });

    client.socket.on('game:rematchReady', (data) => {
        if (data && data.newGameId) {
            const name = client.game?.players?.get(client.playerId)?.name || t('game.defaultPlayerName');
            const colorId = client.game?.players?.get(client.playerId)?.colorId || 'red';
            const virusType = client.game?.players?.get(client.playerId)?.virusType || 'classic';
            client.joinGame(data.newGameId, { playerName: name, colorId, virusType });
        }
    });
}

// New Game button (game-over screen)
const btnNewGame = document.getElementById('btn-new-game');
if (btnNewGame) {
    btnNewGame.addEventListener('click', () => {
        if (client.isSpectating) {
            cleanupSpectatorMode();
            showLandingPage();
            return;
        }
        location.reload();
    });
}

const btnRematch = document.getElementById('btn-rematch');
if (btnRematch) {
    btnRematch.addEventListener('click', () => {
        if (client.socket && client.gameId) {
            client.socket.emit('game:rematch', { gameId: client.gameId });
            btnRematch.disabled = true;
            btnRematch.textContent = t('game.rematchCreating');
        }
    });
}

client.onTurnTimeWarning = () => {
    soundManager.play('turnWarning');
};
client.onTurnTimeout = () => {
    soundManager.play('turnTimeout');
};

let lastTickSecond = -1;
client.onTurnTimeUpdate = (remainingTime) => {
    const remainingSeconds = Math.floor(remainingTime);
    if (remainingSeconds <= 5 && remainingSeconds > 0 && remainingSeconds !== lastTickSecond) {
        lastTickSecond = remainingSeconds;
        soundManager.playTimerTick();
    }
    if (remainingSeconds > 5) lastTickSecond = -1;
};

client.onPillSpawned = () => {
    soundManager.play('pillSpawned');
};
client.onPillActivated = (data) => {
    soundManager.play('pillCollected');
    const player = client.game?.players.get(data.playerId);
    showPillNotification(data.pillType, player?.name || t('game.defaultPlayerName'));
    if (client.renderer) client.renderer.updatePills();
};

// =========================================================================
// GAME JOIN HELPERS
// =========================================================================

async function loadGameColorsForJoin(gameId) {
    try {
        const response = await fetch(`/api/v1/games/${gameId}`);
        if (response.ok) {
            const data = await response.json();
            const takenColors = data.game.players?.map((p) => p.colorId) || [];
            setupColorPicker('join-colors', 'blue', takenColors, onColorSelect);
        } else {
            setupColorPicker('join-colors', 'blue', [], onColorSelect);
        }
    } catch (err) {
        console.warn('[Join] Could not load game info:', err);
        setupColorPicker('join-colors', 'blue', [], onColorSelect);
    }
}

function joinGameById(gameId) {
    const name = document.getElementById('join-name').value || t('game.defaultPlayerName');
    const password = document.getElementById('join-password').value || null;
    client.joinGame(gameId, {
        playerName: name,
        colorId: getSelectedColor('join-colors'),
        virusType: selectedVirusType,
        password
    });
}

// =========================================================================
// FIELD TOOLTIP — Canvas events
// =========================================================================

if (client.renderer) setupTooltipEvents(client);
if (client.rendererReady) client.rendererReady.then(() => setupTooltipEvents(client));

const _isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
let _tooltipHideTimer = null;

canvas.addEventListener('mousemove', (e) => {
    if (_isTouchDevice) return; // Touch devices use tap-based tooltip
    if (fieldTooltip.classList.contains('visible')) {
        fieldTooltip.style.left = `${e.clientX + 15}px`;
        fieldTooltip.style.top = `${e.clientY + 15}px`;
    }
});
canvas.addEventListener('mouseleave', () => {
    if (_isTouchDevice) return;
    fieldTooltip.classList.remove('visible');
});

// Touch tooltip: show on tap for 2 seconds, positioned above tap point
if (_isTouchDevice) {
    let _touchStartForTooltip = null;
    canvas.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
            _touchStartForTooltip = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY
            };
        }
    }, { passive: true });
    canvas.addEventListener('touchend', (e) => {
        if (!_touchStartForTooltip) return;
        const endTouch = e.changedTouches[0];
        const dx = endTouch.clientX - _touchStartForTooltip.x;
        const dy = endTouch.clientY - _touchStartForTooltip.y;
        // Only show tooltip on stationary tap (< 15px movement)
        if (dx * dx + dy * dy < 225) {
            // Pick field to update tooltip content
            if (client.renderer) {
                client.renderer.pickField(endTouch.clientX, endTouch.clientY, false);
            }
            // Position tooltip above the tap point
            if (fieldTooltip.classList.contains('visible')) {
                const tooltipWidth = fieldTooltip.offsetWidth || 180;
                let left = endTouch.clientX - tooltipWidth / 2;
                left = Math.max(8, Math.min(window.innerWidth - tooltipWidth - 8, left));
                fieldTooltip.style.left = `${left}px`;
                fieldTooltip.style.top = `${endTouch.clientY - fieldTooltip.offsetHeight - 20}px`;
                // Auto-hide after 2 seconds
                if (_tooltipHideTimer) clearTimeout(_tooltipHideTimer);
                _tooltipHideTimer = setTimeout(() => {
                    fieldTooltip.classList.remove('visible');
                    _tooltipHideTimer = null;
                }, 2000);
            }
        }
        _touchStartForTooltip = null;
    }, { passive: true });
}

// =========================================================================
// EVENT LISTENERS
// =========================================================================

document.getElementById('btn-play-now').onclick = () => showPanel(mainMenu);
document.getElementById('btn-how-to-play').onclick = () => howToPlayModal.classList.remove('hidden');
document.getElementById('modal-close').onclick = () => howToPlayModal.classList.add('hidden');
document.getElementById('btn-back-landing').onclick = () => showLandingPage();
howToPlayModal.onclick = (e) => {
    if (e.target === howToPlayModal) howToPlayModal.classList.add('hidden');
};

document.querySelectorAll('.tab-btn').forEach((btn) => {
    btn.onclick = () => {
        document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        loadSeasonalLeaderboard(btn.dataset.tab);
    };
});

document.getElementById('btn-create').onclick = () => showPanel(createPanel);
document.getElementById('btn-browse').onclick = () => {
    showPanel(gamesListPanel);
    client.requestGamesList();
};
document.getElementById('btn-join-code').onclick = () => {
    setupColorPicker('join-colors', 'blue', [], onColorSelect);
    showPanel(joinPanel);
};

const joinCodeInput = document.getElementById('join-code');
let codeDebounce = null;
joinCodeInput.addEventListener('input', () => {
    clearTimeout(codeDebounce);
    const code = joinCodeInput.value.toUpperCase().trim();
    if (code.length >= 4) codeDebounce = setTimeout(() => loadGameColorsForJoin(code), 300);
});

document.getElementById('btn-create-back').onclick = () => showPanel(mainMenu);
document.getElementById('btn-games-back').onclick = () => showPanel(mainMenu);
document.getElementById('btn-join-back').onclick = () => showPanel(mainMenu);
document.getElementById('btn-games-refresh').onclick = () => client.requestGamesList();

document.getElementById('btn-create-confirm').onclick = () => {
    const name = document.getElementById('create-name').value.trim() || t('game.defaultPlayerName');
    const maxPlayers = parseInt(document.getElementById('create-max-players').value);
    const slots = slotConfigs.slice(0, maxPlayers).map((s, i) => ({
        index: i,
        type: i === 0 ? SLOT_TYPES.PLAYER : s.type,
        colorId: s.colorId,
        aiDifficulty: s.aiDifficulty
    }));
    saveSettings();
    const isRandomFill = document.getElementById('create-random-fill').checked;
    client.createGame({
        playerName: name,
        colorId: getSelectedColor('create-colors'),
        virusType: selectedVirusType,
        maxPlayers,
        subdivisions: parseInt(document.getElementById('create-subdivisions').value),
        mapPreset: document.getElementById('create-map-preset')?.value || 'classic',
        blockedFields: customBlockedFields.length > 0 ? customBlockedFields : null,
        isPublic: document.getElementById('create-public').checked,
        randomFill: isRandomFill,
        randomFillMin: isRandomFill ? parseInt(document.getElementById('random-fill-min').value) : 0,
        randomFillMax: isRandomFill ? parseInt(document.getElementById('random-fill-max').value) : 0,
        pillsEnabled: document.getElementById('create-pills-enabled').checked,
        slots
    });
};

document.getElementById('btn-join-confirm').onclick = () => {
    const code = document.getElementById('join-code').value.toUpperCase().trim();
    if (code.length >= 4) joinGameById(code);
    else showNotification(t('game.enterValidCode'), 'error');
};

document.getElementById('btn-leave-lobby').onclick = () => {
    client.leaveGame();
    showLandingPage();
};
document.getElementById('btn-start-game').onclick = () => {
    client.startGame();
};
client.onSlotsUpdated = () => {
    _updateLobbyPlayers();
};

const exitModal = document.getElementById('exit-modal');
document.getElementById('btn-exit-game').onclick = () => exitModal.classList.remove('hidden');

// Undo Button
const btnUndo = document.getElementById('btn-undo');
if (btnUndo) {
    btnUndo.onclick = () => {
        if (client.socket && client.gameId) {
            client.undoLastMove();
            btnUndo.disabled = true;
        }
    };

    // Undo-Ergebnis
    client.onUndoResult = (data) => {
        if (data.success) {
            showNotification(t('game.undoSuccess'), 'success');
            btnUndo.style.display = 'none';
            btnUndo._used = true;
        } else {
            btnUndo.disabled = false;
            showNotification(t('game.undoUsed'), 'info');
            btnUndo._used = true;
        }
    };
}
document.getElementById('btn-exit-cancel').onclick = () => exitModal.classList.add('hidden');
document.getElementById('btn-exit-confirm').onclick = () => {
    exitModal.classList.add('hidden');
    client.leaveGame();
    showLandingPage();
    chatPanel.classList.add('hidden');
    quickchatPanel.classList.add('hidden');
    roundIndicator.classList.add('hidden');
    document.getElementById('stats-panel').classList.add('hidden');
    stopGameTimers();
    enableBackgroundEffects();
};
exitModal.addEventListener('click', (e) => {
    if (e.target === exitModal) exitModal.classList.add('hidden');
});

const cameraFollowExplosions = document.getElementById('camera-follow-explosions');
cameraFollowExplosions.onchange = () => {
    client.setCameraFollowExplosions(cameraFollowExplosions.checked);
};
if (client) client.setCameraFollowExplosions(cameraFollowExplosions.checked);

// Chain Reaction Camera Follow toggle
const followChainsCheckbox = document.getElementById('follow-chains-toggle');
if (followChainsCheckbox) {
    // Load persisted value (default: true)
    followChainsCheckbox.checked = localStorage.getItem('boomfection_follow_chains') !== 'false';
    followChainsCheckbox.onchange = () => {
        localStorage.setItem('boomfection_follow_chains', followChainsCheckbox.checked);
        if (!followChainsCheckbox.checked && client.renderer) {
            client.renderer.stopChainFollow();
        }
    };
}

document.getElementById('chat-send').onclick = () => {
    const input = document.getElementById('chat-input');
    if (input.value.trim()) {
        client.sendChatMessage(input.value);
        input.value = '';
    }
};
document.getElementById('chat-input').onkeypress = (e) => {
    if (e.key === 'Enter') document.getElementById('chat-send').click();
};

document.querySelectorAll('.quickchat-btn').forEach((btn) => {
    btn.onclick = () => {
        const emote = btn.dataset.emote;
        if (emote) client.sendQuickChat(emote);
    };
});

/**
 * Keyboard-Shortcuts fuer Navigation und In-Game Aktionen
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
document.addEventListener('keydown', (e) => {
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
        if (e.key === 'Escape') document.activeElement.blur();
        return;
    }

    if (e.key === 'Escape') {
        if (!winnerOverlay.classList.contains('hidden')) return;
        if (!gameUI.classList.contains('hidden')) {
            // Spectator mode: ESC goes directly back to landing
            if (client.isSpectating) {
                cleanupSpectatorMode();
                showLandingPage();
                return;
            }
            const exitM = document.getElementById('exit-modal');
            if (exitM) exitM.classList.toggle('hidden');
            return;
        }
        if (
            !createPanel.classList.contains('hidden') ||
            !gamesListPanel.classList.contains('hidden') ||
            !joinPanel.classList.contains('hidden')
        ) {
            showPanel(mainMenu);
        } else if (!lobbyPanel.classList.contains('hidden')) {
            client.leaveGame();
            showPanel(mainMenu);
        }
    }
    if (e.key === 'Enter' && !gameUI.classList.contains('hidden')) {
        const chatInput = document.getElementById('chat-input');
        if (document.activeElement !== chatInput) {
            e.preventDefault();
            chatInput.focus();
        }
    }

    if (!client.game || gameUI.classList.contains('hidden')) return;
    if ((e.key === 'r' || e.key === 'R') && client.renderer && client.renderer.camera) {
        client.renderer.camera.position.set(0, 0, 3.2);
        if (client.renderer.controls) client.renderer.controls.reset();
    }

    if (e.key === 'Tab') {
        e.preventDefault();
        if (!client.game || !client.game.sphere) return;
        const myFields = [];
        for (const field of client.game.sphere.fields.values()) {
            if (field.ownerId === client.playerId) myFields.push(field);
        }
        if (myFields.length > 0 && client.renderer?.rotateToField) {
            const nextIdx = ((window._tabFieldIdx || 0) + 1) % myFields.length;
            window._tabFieldIdx = nextIdx;
            client.renderer.rotateToField(myFields[nextIdx].id);
        }
    }
});

// =========================================================================
// FEATURE: RANKED MATCHMAKING
// =========================================================================

/**
 * Laedt den Ranked-Status des Spielers und zeigt Season/Rank-Info an
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 * @param {string} nickname - Spielername
 */
async function loadRankedStatus(nickname) {
    if (!nickname) return;

    try {
        const res = await fetch(`/api/v1/ranked/status/${encodeURIComponent(nickname)}`);
        if (!res.ok) return;

        const data = await res.json();

        // Ranked deaktiviert — Button verstecken
        const btnRanked = document.getElementById('btn-ranked');
        if (!data.enabled) {
            if (btnRanked) btnRanked.classList.add('hidden');
            return;
        }
        if (btnRanked) btnRanked.classList.remove('hidden');

        // Season-Info anzeigen
        const seasonNameEl = document.getElementById('ranked-season-name');
        const seasonRemainingEl = document.getElementById('ranked-season-remaining');
        if (data.season && seasonNameEl) {
            seasonNameEl.textContent = t('ranked.seasonName', { label: data.season.label || data.season.id });
        }
        if (data.season && seasonRemainingEl) {
            seasonRemainingEl.textContent = t('ranked.daysRemaining', { days: data.season.daysRemaining || 0 });
        }

        // Placement vs Rank anzeigen
        const placementContainer = document.getElementById('ranked-placement-container');
        const rankContainer = document.getElementById('ranked-rank-container');

        if (data.rank && data.placementComplete) {
            // Rang anzeigen
            if (placementContainer) placementContainer.classList.add('hidden');
            if (rankContainer) rankContainer.classList.remove('hidden');

            const badgeEl = document.getElementById('ranked-rank-badge');
            const eloEl = document.getElementById('ranked-rank-elo');
            const recordEl = document.getElementById('ranked-rank-record');

            if (badgeEl) badgeEl.textContent = t('ranked.rankPosition', { rank: data.rank.rank || '-' });
            if (eloEl) eloEl.textContent = t('ranked.eloDisplay', { elo: data.rank.elo || 1000 });
            if (recordEl) recordEl.textContent = t('ranked.record', { wins: data.rank.wins || 0, losses: data.rank.losses || 0 });
        } else {
            // Placement-Fortschritt anzeigen
            if (rankContainer) rankContainer.classList.add('hidden');
            if (placementContainer) placementContainer.classList.remove('hidden');

            const gamesPlayed = data.rank?.gamesPlayed || 0;
            const totalNeeded = data.placementGames || 5;
            const pct = Math.min(100, Math.round((gamesPlayed / totalNeeded) * 100));

            const barEl = document.getElementById('ranked-placement-bar');
            const textEl = document.getElementById('ranked-placement-text');
            if (barEl) barEl.style.width = `${pct}%`;
            if (textEl) textEl.textContent = t('ranked.placementCount', { current: gamesPlayed, total: totalNeeded });
        }
    } catch {
        /* Netzwerkfehler — stille Behandlung, Panel bleibt funktional */
    }
}

document.getElementById('btn-ranked')?.addEventListener('click', () => {
    if (!client.socket) return showNotification(t('game.notConnected'), 'error');
    const playerName = (document.getElementById('create-name')?.value?.trim()) || t('game.defaultPlayerName');
    showPanel(matchmakingPanel);

    // Ranked-Status laden und anzeigen
    loadRankedStatus(playerName);

    // Queue beitreten
    client.socket.emit('matchmaking:join', { nickname: playerName });
});

document.getElementById('btn-matchmaking-cancel')?.addEventListener('click', () => {
    if (client.socket) client.socket.emit('matchmaking:leave');
    showPanel(mainMenu);
});

// =========================================================================
// FEATURE: GAMIFICATION NOTIFICATIONS
// =========================================================================

/**
 * Registriert Socket-Events fuer XP, Level-Up und Rang-Benachrichtigungen
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
function setupNotificationListeners() {
    if (!client.socket) return;

    // Zuschauer-Emotes (Floating-Emoji über dem Spielfeld)
    client.socket.on('spectator:emote', (data) => {
        if (!data?.emote || !client.virusRenderer) return;
        client.virusRenderer.showFloatingEmote(data.emote, data.sender);
    });

    client.socket.on('notification', (data) => {
        if (!data) return;

        if (data.type === 'level_up') {
            showNotification(t('game.levelUp', { level: data.data.level }), 'success');
            if (typeof soundManager !== 'undefined') soundManager.play('achievement');
        } else if (data.type === 'rank_up') {
            showNotification(
                t('game.rankUp', { rank: data.data.rank.charAt(0).toUpperCase() + data.data.rank.slice(1) }),
                'success'
            );
            if (typeof soundManager !== 'undefined') soundManager.play('victory');
        } else {
            showNotification(data.message || data.title, 'info');
        }
    });

    client.socket.on('xp:update', (data) => {
        if (!data || !data.level) return;

        if (data.gained) {
            showNotification(t('game.xpGained', { xp: data.gained }), 'info');
        }

        const xpBar = document.getElementById('xp-progress');
        if (xpBar) {
            const nextLevelXP = (data.level + 1) * (data.level + 1) * 10;
            const currentLevelXP = data.level * data.level * 10;
            const progress = ((data.xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
            xpBar.style.width = `${Math.min(Math.max(progress, 0), 100)}%`;
        }

        const levelDisplay = document.getElementById('player-level');
        if (levelDisplay) {
            levelDisplay.textContent = data.level;
        }

        const rankDisplay = document.getElementById('player-rank');
        if (rankDisplay) {
            rankDisplay.textContent = data.rank;
        }
    });
}

function setupMatchmakingListeners() {
    if (!client.socket) return;

    client.socket.on('matchmaking:joined', (data) => {
        const mmStatusEl = document.getElementById('matchmaking-status');
        const eloEl = document.getElementById('matchmaking-elo');
        const queueEl = document.getElementById('matchmaking-queue');
        if (mmStatusEl) mmStatusEl.textContent = t('game.matchmakingSearching');
        if (eloEl) eloEl.textContent = t('game.matchmakingElo', { elo: data.elo || 1000 });
        if (queueEl) queueEl.textContent = t('game.matchmakingPosition', { pos: data.position || 1 });

        // ELO-Range anzeigen
        const rangeEl = document.getElementById('matchmaking-elo-range');
        if (rangeEl && data.eloRange) {
            rangeEl.textContent = t('ranked.eloRange', { range: data.eloRange });
        }
    });

    // Periodische Status-Updates
    client.socket.on('matchmaking:status', (data) => {
        const queueEl = document.getElementById('matchmaking-queue');
        const waitEl = document.getElementById('matchmaking-wait-time');
        const rangeEl = document.getElementById('matchmaking-elo-range');
        const eloEl = document.getElementById('matchmaking-elo');

        if (queueEl) queueEl.textContent = t('game.matchmakingPosition', { pos: data.position || 1 });
        if (waitEl) waitEl.textContent = t('ranked.waitTime', { seconds: data.waitTime || 0 });
        if (rangeEl) rangeEl.textContent = t('ranked.eloRange', { range: data.eloRange || 200 });
        if (eloEl) eloEl.textContent = t('game.matchmakingElo', { elo: data.elo || 1000 });
    });

    // Match gefunden — Server hat Spiel bereits erstellt, direkt zum Game navigieren
    client.socket.on('matchmaking:found', (data) => {
        const mmStatusEl = document.getElementById('matchmaking-status');
        if (mmStatusEl) mmStatusEl.textContent = t('game.matchFound', { opponent: data.opponent });
        showNotification(t('game.matchFoundNotif', { opponent: data.opponent }), 'success');

        // Spiel wurde serverseitig erstellt — Client navigiert direkt zum Spiel
        if (data.gameId) {
            setTimeout(() => {
                window.location.href = `/game?id=${data.gameId}`;
            }, 1500);
        } else {
            // Fallback: Client erstellt Spiel (Abwaertskompatibilitaet)
            setTimeout(() => {
                const playerName = (document.getElementById('create-name')?.value?.trim()) || t('game.defaultPlayerName');
                client.createGame({
                    playerName,
                    colorId: selectedColor,
                    virusType: selectedVirusType,
                    maxPlayers: data.gameConfig.maxPlayers,
                    subdivisions: data.gameConfig.subdivisions,
                    isPublic: false,
                    gameMode: data.gameConfig.gameMode || 'standard'
                });
            }, 1500);
        }
    });

    // Matchmaking-Fehler
    client.socket.on('matchmaking:error', (data) => {
        const errorKey = data?.error || 'internal_error';
        showNotification(t(`ranked.error.${errorKey}`, t('common.error')), 'error');
        showPanel(mainMenu);
    });
}

// =========================================================================
// FEATURE: CAMPAIGN
// =========================================================================

document.getElementById('btn-campaign').onclick = () => {
    showPanel(campaignPanel);
    renderCampaignLevels(client, selectedColor, selectedVirusType);
};

document.getElementById('btn-campaign-back').onclick = () => showPanel(mainMenu);

// =========================================================================
// FEATURE: FRIENDS SYSTEM
// =========================================================================

let currentPlayerNickname = null;

document.getElementById('btn-friends-toggle')?.addEventListener('click', function () {
    if (friendsPanel) {
        friendsPanel.classList.toggle('hidden');
        if (!friendsPanel.classList.contains('hidden')) {
            loadFriendsList(currentPlayerNickname);
        }
    }
});

document.getElementById('btn-friends-close')?.addEventListener('click', function () {
    if (friendsPanel) friendsPanel.classList.add('hidden');
});

document.getElementById('btn-friend-add')?.addEventListener('click', async function () {
    const input = document.getElementById('friend-add-name');
    const friendName = input?.value?.trim();
    const myName = document.getElementById('create-name')?.value?.trim() || currentPlayerNickname;

    if (!friendName || !myName) {
        showNotification(t('friends.enterPlayerName'), 'error');
        return;
    }

    try {
        const res = await fetch('/api/friends/request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userNickname: myName, friendNickname: friendName })
        });
        const data = await res.json();

        if (data.success) {
            showNotification(t('friends.requestSent'), 'success');
            if (input) input.value = '';
            loadFriendsList(currentPlayerNickname);
        } else {
            showNotification(data.error || t('common.error'), 'error');
        }
    } catch (err) {
        console.error('[Friends] Request error:', err);
        showNotification(t('game.connectionError'), 'error');
    }
});

// =========================================================================
// FEATURE: MAP PRESET HANDLER
// =========================================================================

const mapPresetSelect = document.getElementById('create-map-preset');
const subdivisionsSelect = document.getElementById('create-subdivisions');
const mapEditorBtn = document.getElementById('btn-open-map-editor');

/** @type {number[]} Aktuelle Custom-Map Blocker (leer wenn kein Custom) */
let customBlockedFields = [];
/** @type {MapEditor|null} Editor-Instanz (lebt nur während Modal offen ist) */
let mapEditorInstance = null;

/** @type {Map<string, {subdivisions: number}>} slug → map meta für DB-Maps */
const dbMapSubdivisions = new Map();

/**
 * Lädt published Admin-Maps und hängt sie an das Preset-Dropdown
 */
async function loadAdminMapsIntoDropdown() {
    if (!mapPresetSelect) return;
    try {
        const res = await fetch('/api/v1/maps');
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success || !Array.isArray(data.maps) || data.maps.length === 0) return;

        // Separator-Option
        const separator = document.createElement('option');
        separator.disabled = true;
        separator.textContent = `──────── ${  t('landing.mapEditorAdminMaps')  } ────────`;
        mapPresetSelect.appendChild(separator);

        for (const map of data.maps) {
            const opt = document.createElement('option');
            opt.value = `db:${map.slug}`;
            opt.textContent = map.name;
            if (map.description) opt.title = map.description;
            mapPresetSelect.appendChild(opt);
            dbMapSubdivisions.set(map.slug, { subdivisions: map.subdivisions });
        }
    } catch {
        /* silent — Admin-Maps sind optional */
    }
}
loadAdminMapsIntoDropdown();

if (mapPresetSelect && subdivisionsSelect) {
    // Zentrale Preset-Config (Single Source of Truth: src/game/config/MapPresets.js)
    const PRESET_SUBDIVISIONS = { classic: 3, ring: 3, duel: 2, fortress: 3, chaos: 3, arena: 4 };

    mapPresetSelect.addEventListener('change', function () {
        const value = mapPresetSelect.value;

        // Custom Map: Editor-Button anzeigen, Slider aktiv lassen
        if (value === 'custom') {
            if (mapEditorBtn) mapEditorBtn.classList.remove('hidden');
            subdivisionsSelect.disabled = false;
            subdivisionsSelect.style.opacity = '1';
            openMapEditor();
            return;
        }

        // Preset-Wechsel weg von custom → Custom-State löschen
        if (customBlockedFields.length > 0) {
            customBlockedFields = [];
        }
        if (mapEditorBtn) mapEditorBtn.classList.add('hidden');

        // DB-Admin-Map ("db:<slug>"): Custom-State löschen, Subdivision aus DB-Meta
        if (value.startsWith('db:')) {
            customBlockedFields = [];
            const slug = value.slice(3);
            const meta = dbMapSubdivisions.get(slug);
            if (meta) {
                subdivisionsSelect.value = String(meta.subdivisions);
            }
            subdivisionsSelect.disabled = true;
            subdivisionsSelect.style.opacity = '0.5';
            return;
        }

        const preset = { subdivision: PRESET_SUBDIVISIONS[value] || 3 };
        subdivisionsSelect.value = String(preset.subdivision);
        // Bei Presets mit fester Subdivision: Slider disablen
        const isFixed = value !== 'classic';
        subdivisionsSelect.disabled = isFixed;
        subdivisionsSelect.style.opacity = isFixed ? '0.5' : '1';
    });
}

// Editor-Button: Erneut öffnen um Map zu bearbeiten
if (mapEditorBtn) {
    mapEditorBtn.addEventListener('click', openMapEditor);
}

/**
 * Öffnet das Map-Editor-Modal und initialisiert eine MapEditor-Instanz
 */
function openMapEditor() {
    const modal = document.getElementById('map-editor-modal');
    const canvas = document.getElementById('map-editor-canvas');
    if (!modal || !canvas) return;

    modal.classList.remove('hidden');

    // Vorherige Instanz sauber aufräumen (verhindert Listener-Leak)
    if (mapEditorInstance) {
        mapEditorInstance.destroy();
        mapEditorInstance = null;
    }

    const subdivisions = parseInt(subdivisionsSelect?.value || '3', 10);
    mapEditorInstance = new MapEditor(canvas, { subdivisions });

    // Bestehende Selektion wiederherstellen (ein buildGeometry statt N)
    if (customBlockedFields.length > 0) {
        mapEditorInstance.loadFromData({ subdivisions, blockedFields: customBlockedFields });
    }

    // Live Stats updaten
    const statBlocked = document.getElementById('map-editor-stat-blocked');
    const statTotal = document.getElementById('map-editor-stat-total');
    const statWarning = document.getElementById('map-editor-stat-warning');

    const updateStats = (_blockedFields, stats) => {
        if (statBlocked) statBlocked.textContent = String(stats.blocked);
        if (statTotal) statTotal.textContent = String(stats.total);
        if (statWarning) statWarning.classList.toggle('hidden', !stats.overLimit);
    };
    mapEditorInstance.onChange(updateStats);
    updateStats(mapEditorInstance.getBlockedFields(), mapEditorInstance.getStats());

    // Mirror-Toggle
    const mirrorCheckbox = document.getElementById('map-editor-mirror');
    if (mirrorCheckbox) {
        mirrorCheckbox.checked = false;
        mirrorCheckbox.onchange = () => mapEditorInstance.setMirrorMode(mirrorCheckbox.checked);
    }

    // Button-Handler
    const clearBtn = document.getElementById('map-editor-clear');
    const invertBtn = document.getElementById('map-editor-invert');
    const cancelBtn = document.getElementById('map-editor-cancel');
    const saveBtn = document.getElementById('map-editor-save');
    const closeBtn = document.getElementById('map-editor-close');

    if (clearBtn) clearBtn.onclick = () => mapEditorInstance?.clear();
    if (invertBtn) invertBtn.onclick = () => mapEditorInstance?.invert();

    // Saved Maps: Render + Save + Load + Delete
    const savedList = document.getElementById('map-editor-saved-list');
    const saveLocalBtn = document.getElementById('map-editor-save-local');

    const renderSavedList = () => {
        if (!savedList) return;
        const maps = loadSavedMaps();
        if (maps.length === 0) {
            savedList.innerHTML = `<div class="map-editor-saved-empty">${t('landing.mapEditorSavedEmpty')}</div>`;
            return;
        }
        savedList.innerHTML = '';
        for (const map of maps) {
            const item = document.createElement('div');
            item.className = 'map-editor-saved-item';

            const name = document.createElement('span');
            name.className = 'map-editor-saved-name';
            name.textContent = map.name;
            name.title = t('landing.mapEditorSavedLoad');
            name.onclick = () => mapEditorInstance.loadFromData(map);

            const meta = document.createElement('span');
            meta.className = 'map-editor-saved-meta';
            meta.textContent = `sub${map.subdivisions} · ${map.blockedFields.length}`;

            const del = document.createElement('button');
            del.className = 'map-editor-saved-delete';
            del.textContent = '×';
            del.title = t('landing.mapEditorSavedDelete');
            del.onclick = (e) => {
                e.stopPropagation();
                deleteSavedMap(map.id);
                renderSavedList();
            };

            item.appendChild(name);
            item.appendChild(meta);
            item.appendChild(del);
            savedList.appendChild(item);
        }
    };

    renderSavedList();

    if (saveLocalBtn) {
        saveLocalBtn.onclick = () => {
            const stats = mapEditorInstance.getStats();
            if (stats.blocked === 0) {
                showNotification(t('landing.mapEditorSaveEmpty'), 'warning');
                return;
            }
            const defaultName = `${t('landing.mapEditorDefaultName')} ${new Date().toLocaleDateString()}`;
            const name = prompt(t('landing.mapEditorSavePrompt'), defaultName);
            if (!name) return;
            saveMap({
                name,
                subdivisions: mapEditorInstance.subdivisions,
                blockedFields: mapEditorInstance.getBlockedFields()
            });
            renderSavedList();
            showNotification(t('landing.mapEditorSaveSuccess'), 'success');
        };
    }

    const closeEditor = (save) => {
        if (save && mapEditorInstance) {
            customBlockedFields = mapEditorInstance.getBlockedFields();
        } else if (!save) {
            // Abbruch: Wenn keine Selektion → zurück zu classic
            if (customBlockedFields.length === 0) {
                mapPresetSelect.value = 'classic';
                if (mapEditorBtn) mapEditorBtn.classList.add('hidden');
                mapPresetSelect.dispatchEvent(new Event('change'));
            }
        }
        if (mapEditorInstance) {
            mapEditorInstance.destroy();
            mapEditorInstance = null;
        }
        modal.classList.add('hidden');
    };

    if (saveBtn) saveBtn.onclick = () => closeEditor(true);
    if (cancelBtn) cancelBtn.onclick = () => closeEditor(false);
    if (closeBtn) closeBtn.onclick = () => closeEditor(false);
}

// =========================================================================
// PLAYER STATS DETAIL (Winner overlay)
// =========================================================================

/**
 * Rendert detaillierte Spielerstatistiken im Winner-Overlay
 * @param {Array} players - Spieler-Array aus game:ended Event
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */
function renderPlayerStatsDetail(players) {
    const container = document.getElementById('player-stats-detail');
    if (!container || !players || players.length === 0) return;

    const sorted = players.slice().sort((a, b) => (b.points || 0) - (a.points || 0));

    container.textContent = '';

    const title = document.createElement('div');
    title.className = 'stats-detail-title';
    title.textContent = t('game.playerStats');
    container.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'stats-detail-grid';

    const header = document.createElement('div');
    header.className = 'stats-header';
    ['', t('game.statTurns'), t('game.statFields'), t('game.statChain'), t('game.statPoints')].forEach((label) => {
        const span = document.createElement('span');
        span.textContent = label;
        header.appendChild(span);
    });
    grid.appendChild(header);

    sorted.forEach((p) => {
        const stats = p.stats || {};
        const row = document.createElement('div');
        row.className = 'stats-row';

        const nameSpan = document.createElement('span');
        nameSpan.className = 'stats-player';
        nameSpan.style.color = (p.color?.hex || '#888').replace(/[^#0-9a-fA-F]/g, '');
        nameSpan.textContent = p.name || '';

        const turnsSpan = document.createElement('span');
        turnsSpan.textContent = stats.turnsPlayed || 0;
        const fieldsSpan = document.createElement('span');
        fieldsSpan.textContent = stats.fieldsConquered || 0;
        const chainSpan = document.createElement('span');
        chainSpan.textContent = stats.longestChain || 0;
        const pointsSpan = document.createElement('span');
        pointsSpan.textContent = p.points || 0;

        row.appendChild(nameSpan);
        row.appendChild(turnsSpan);
        row.appendChild(fieldsSpan);
        row.appendChild(chainSpan);
        row.appendChild(pointsSpan);
        grid.appendChild(row);
    });

    container.appendChild(grid);
}

// Explosion effect callback — uses imported createExplosionEffect
const originalOnExplosion = client.onExplosion;
client.onExplosion = (data) => {
    if (originalOnExplosion) originalOnExplosion(data);
    const rect = canvas.getBoundingClientRect();
    createExplosionEffect(
        rect.left + rect.width / 2 + (Math.random() - 0.5) * 200,
        rect.top + rect.height / 2 + (Math.random() - 0.5) * 200
    );
};

// =========================================================================
// AUTH
// =========================================================================

let _currentUser = null;

Navigation.onAuthChange((user) => {
    _currentUser = user;

    // Name fields: logged-in users use their display name (not editable)
    const createNameInput = document.getElementById('create-name');
    const joinNameInput = document.getElementById('join-name');
    const createNameGroup = createNameInput?.closest('.form-group') || createNameInput?.parentElement;
    const joinNameGroup = joinNameInput?.closest('.form-group') || joinNameInput?.parentElement;

    if (user && user.role !== 'guest') {
        const displayName = user.displayName || user.username || '';
        currentPlayerNickname = displayName;
        if (createNameInput) { createNameInput.value = displayName; createNameInput.disabled = true; }
        if (joinNameInput) { joinNameInput.value = displayName; joinNameInput.disabled = true; }
        // Hide the label/group to save space (name is shown in header)
        if (createNameGroup) createNameGroup.style.display = 'none';
        if (joinNameGroup) joinNameGroup.style.display = 'none';
    } else {
        // Guest: enable name input
        if (createNameInput) createNameInput.disabled = false;
        if (joinNameInput) joinNameInput.disabled = false;
        if (createNameGroup) createNameGroup.style.display = '';
        if (joinNameGroup) joinNameGroup.style.display = '';
    }

    // On page load: if user is logged in, sync preferences from server
    if (user) {
        PreferencesService.syncFromServer()
            .then((synced) => {
                if (synced) {
                    const freshSettings = loadSettings();
                    if (freshSettings) applySettings(freshSettings);
                }
            })
            .catch(() => {});
    }
});
initAuthModals(showNotification);

function _setLoggedInUser(user) {
    Navigation.setUser(user);
}
function _clearLoggedInUser() {
    Navigation.clearUser();
}

// =========================================================================
// INITIALIZATION
// =========================================================================

async function init() {
    const reconnectOverlay = document.getElementById('reconnect-overlay');
    // Landing Page sofort zeigen (UI), aber Stats-Refresh erst nach Socket-Connect
    showPanel(document.getElementById('landing-page'));
    try {
        await client.connect();
        // Stats erst NACH Socket-Verbindung laden (sonst onlinePlayers=0)
        showLandingPage();
        client.startRenderLoop();
        setupChatReactionAndRematchEvents();
        setupNotificationListeners();
        setupMatchmakingListeners();
        initAchievementListener(client.socket, soundManager);

        // Camera-Return-Icon
        initCameraReturnBtn(client);
        if (client.renderer) {
            const origOnCameraChange = client.renderer.onCameraChange;
            client.renderer.onCameraChange = () => {
                hideCameraReturnBtn();
                if (origOnCameraChange) origOnCameraChange();
            };
            const origOnFieldClick = client.renderer.onFieldClick;
            client.renderer.onFieldClick = (fieldId) => {
                hideCameraReturnBtn();
                if (origOnFieldClick) origOnFieldClick(fieldId);
            };
        }
        const hasSession = client.loadSession();
        if (hasSession) {
            reconnectOverlay.classList.remove('hidden');
            showPanel(null);
            const reconnected = await client.tryReconnect();
            reconnectOverlay.classList.add('hidden');
            if (reconnected) showNotification(t('game.reconnectSuccess'), 'success');
            else showLandingPage();
        }
    } catch (err) {
        console.error('Connection failed:', err);
        showNotification(t('game.connectFailed'), 'error');
        // Auch bei Fehler Landing Page mit Stats zeigen
        showLandingPage();
    }

    // Guest config laden (fuer Nudge-Timing und Mode-Restrictions)
    try {
        const gcRes = await fetch('/api/v1/guest/config');
        if (gcRes.ok) _guestConfig = await gcRes.json();
    } catch {
        /* expected — network error or server unavailable; guestConfig stays null, defaults apply */
    }

    // Daily Reward Widget (nur fuer eingeloggte User)
    renderDailyRewardWidget(document.getElementById('daily-reward-widget'));

    // Cookie Consent Banner (DSGVO)
    cookieConsent.show();

    // AdSense (nur bei Marketing-Consent)
    adManager.init('ca-pub-XXXXXXX'); // TODO: Replace with actual AdSense Client-ID
    adManager.registerSlot('ad-slot-landing');

    // Replay-Watch-Buttons
    initReplayButtons(async (gameId) => {
        showNotification(t('game.replayLoading') || 'Replay wird geladen...', 'info');
        try {
            const res = await fetch(`/api/games/${encodeURIComponent(gameId)}/replay`);
            if (!res.ok) throw new Error('Replay not found');
            const data = await res.json();
            if (data.events && data.events.length > 0) {
                playReplay(data);
            } else {
                showNotification(t('game.replayEmpty') || 'Keine Replay-Daten', 'info');
            }
        } catch {
            showNotification(t('game.replayError') || 'Replay konnte nicht geladen werden', 'error');
        }
    });
}

// =========================================================================
// REPLAY PLAYER (spielt aufgezeichnete Spiele auf der Sphäre ab)
// =========================================================================

let _replayTimer = null;

async function playReplay(replayData) {
    // Laufenden Replay stoppen
    if (_replayTimer) {
        clearInterval(_replayTimer);
        _replayTimer = null;
    }

    // Lokales Spiel erstellen für die Replay-Darstellung
    const config = {
        subdivisions: replayData.mapSize || 3,
        players: [
            { name: replayData.winner || t('game.defaultPlayerNumbered', { n: 1 }), color: 'red', isAI: false },
            { name: t('game.opponent'), color: 'blue', isAI: true }
        ]
    };

    try {
        await client.createLocalGame(config);
    } catch {
        showNotification(t('game.replayStartError') || 'Replay konnte nicht gestartet werden', 'error');
        return;
    }

    showNotification(`▶ Replay: ${replayData.winner} (${replayData.events.length} Events)`, 'success');

    // Events zeitgesteuert abspielen
    const events = replayData.events;
    let idx = 0;
    _replayTimer = setInterval(() => {
        if (idx >= events.length) {
            clearInterval(_replayTimer);
            _replayTimer = null;
            showNotification(t('game.replayFinished') || 'Replay beendet', 'success');
            return;
        }

        const event = events[idx];

        if (event.type === 'move' && client.game) {
            const field = client.game.sphere.getField(event.fieldId);
            if (field) {
                field.value = event.value || field.value + 1;
                field.ownerId = event.ownerId;
                if (client.renderer) {
                    client.renderer.updateColors();
                    client.renderer.updatePills();
                }
                if (client.virusRenderer) {
                    client.virusRenderer.triggerFieldPulse(event.fieldId, '#ff8c32');
                }
            }
        } else if (event.type === 'explosion' && client.virusRenderer) {
            client.virusRenderer.triggerExplosion(event.fieldId, '#ff4400');
        }

        idx++;
    }, 200); // 200ms pro Event (5 Events/Sekunde)
}

// =========================================================================
// QUICK-MATCH BUTTON
// =========================================================================

const btnQuickMatch = document.getElementById('btn-quick-match');
if (btnQuickMatch) {
    btnQuickMatch.addEventListener('click', () => {
        // Guest nudge: inform non-logged-in users they play as guest
        if (!Navigation.getUser()) {
            showNotification(t('game.playingAsGuest'), 'info');
        }

        btnQuickMatch.disabled = true;
        btnQuickMatch.textContent = t('game.quickMatchSearching');

        // Sicherstellen, dass Socket verbunden ist
        if (!client.socket || !client.socket.connected) {
            client.connect().then(() => _tryQuickMatch()).catch(() => {
                btnQuickMatch.disabled = false;
                btnQuickMatch.textContent = t('game.quickMatch');
                showNotification(t('game.connectFailed'), 'error');
            });
        } else {
            _tryQuickMatch();
        }
    });
}

function _tryQuickMatch() {
    // Offene Spiele abfragen
    fetch('/api/v1/games')
        .then((res) => res.json())
        .then((data) => {
            const openGames = (data.games || []).filter(
                (g) => g.state === 'waiting' && g.players < g.maxPlayers && !g.password
            );

            if (openGames.length > 0) {
                // Erstes offenes Spiel joinen
                const game = openGames[0];
                document.getElementById('join-code').value = game.id;
                const name = document.getElementById('create-name').value.trim() || t('game.defaultPlayerName');
                client.joinGame(game.id, {
                    playerName: name,
                    colorId: selectedColor || 'red',
                    virusType: selectedVirusType || 'classic'
                });
            } else {
                // Neues Spiel mit Defaults erstellen
                const name = document.getElementById('create-name').value.trim() || t('game.defaultPlayerName');
                client.createGame({
                    playerName: name,
                    colorId: selectedColor || 'red',
                    virusType: selectedVirusType || 'classic',
                    subdivisions: 3,
                    maxPlayers: 6,
                    isPublic: true,
                    pillsEnabled: true,
                    gameMode: 'standard',
                    slots: [
                        { type: 'player' },
                        { type: 'ai', aiDifficulty: 'medium' },
                        { type: 'player' },
                        { type: 'player' },
                        { type: 'player' }
                    ]
                });
            }

            btnQuickMatch.disabled = false;
            btnQuickMatch.textContent = t('game.quickMatch');
        })
        .catch(() => {
            btnQuickMatch.disabled = false;
            btnQuickMatch.textContent = t('game.quickMatch');
            showNotification(t('game.connectFailed'), 'error');
        });
}

// Re-render daily reward widget on auth change
Navigation.onAuthChange(() => {
    renderDailyRewardWidget(document.getElementById('daily-reward-widget'));
});

initAllEffects();

// =========================================================================
// Mobile Utilities: Rotate Hint dismiss + Fullscreen toggle
// =========================================================================

(function initMobileUtils() {
    // Rotate hint dismiss
    const rotateHintDismiss = document.getElementById('rotate-hint-dismiss');
    const rotateHint = document.getElementById('rotate-hint');
    if (rotateHintDismiss && rotateHint) {
        rotateHintDismiss.addEventListener('click', () => {
            rotateHint.classList.add('dismissed');
        });
    }

    // Fullscreen toggle (only on touch devices)
    const fullscreenBtn = document.getElementById('fullscreen-btn');
    if (fullscreenBtn && window.matchMedia('(pointer: coarse)').matches) {
        const updateIcon = () => {
            fullscreenBtn.textContent = document.fullscreenElement ? '\u2716' : '\u26F6';
        };
        fullscreenBtn.addEventListener('click', () => {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(() => {});
            } else {
                document.documentElement.requestFullscreen().catch(() => {});
            }
        });
        document.addEventListener('fullscreenchange', updateIcon);
    }
})();

init();
